import { memo, useEffect, useRef } from 'react'
import { prefersReducedMotion } from '../../../shared/motion.ts'
import flightMarker from '../../../shared/assets/crash-plane.png'
import stageBackground from '../../../shared/assets/crash-world.webp'

type Point = { t: number; m: number }

type ChartLabels = {
  waiting: string
  flying: string
  crashAt: (mult: number) => string
  aria: string
}

type Props = {
  points: Point[]
  multiplier: number
  crashed: boolean
  crashPoint: number | null
  phase?: string
  /** Changes every round; resets axis domains. */
  roundId?: string | null
  labels?: ChartLabels
}

type Particle = {
  x: number
  y: number
  vx: number
  vy: number
  born: number
  life: number
  size: number
  color: string
  rotation: number
  spin: number
  kind: 'mote' | 'shard'
}

type TrailPoint = {
  x: number
  y: number
  angle: number
  born: number
}

type MotionState = {
  x: number
  y: number
  angle: number
  multiplier: number
  initialized: boolean
  lastNow: number
  crashX: number
  crashY: number
  crashAngle: number
}

const SPRITE_HEADING = -Math.PI * 0.16
const PLOT_TOP = 88

function damp(current: number, target: number, response: number, dt: number) {
  return current + (target - current) * (1 - Math.exp(-response * dt))
}

function dampAngle(current: number, target: number, response: number, dt: number) {
  let delta = (target - current + Math.PI) % (Math.PI * 2) - Math.PI
  if (delta < -Math.PI) delta += Math.PI * 2
  return current + delta * (1 - Math.exp(-response * dt))
}

/** Smooth polyline through points via midpoint quadratic curves. */
function traceSmooth(
  path: Path2D,
  pts: Point[],
  toX: (t: number) => number,
  toY: (m: number) => number,
) {
  pts.forEach((p, i) => {
    const x = toX(p.t)
    const y = toY(p.m)
    if (i === 0) {
      path.moveTo(x, y)
      return
    }
    if (i === pts.length - 1) {
      path.lineTo(x, y)
      return
    }
    const next = pts[i + 1]
    path.quadraticCurveTo(x, y, (x + toX(next.t)) / 2, (y + toY(next.m)) / 2)
  })
}

/**
 * Display mapping (server remains authoritative for t/m values):
 * - X grows with elapsed; domain only expands within a round so the rocket
 *   flies toward upper-right instead of always sitting on the right edge.
 * - Y maps multiplier from 1.0 with headroom above the live mult.
 */
const defaultLabels: ChartLabels = {
  waiting: 'Preparing next round',
  flying: 'Live flight',
  crashAt: (m) => `${m.toFixed(2)}x`,
  aria: 'chart',
}

const TRAIL_COLORS = ['#ff8b79', '#f1a9c1', '#79d6e4']
const BURST_COLORS = ['#ff8b79', '#f0a5bc', '#86dce6', '#f8d8df']

export const CrashChart = memo(function CrashChart({
  points,
  multiplier,
  crashed,
  crashPoint,
  phase,
  roundId,
  labels = defaultLabels,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const markerRef = useRef<HTMLImageElement | null>(null)
  const backgroundRef = useRef<HTMLImageElement | null>(null)
  const rafRef = useRef<number>(0)
  const propsRef = useRef({ points, multiplier, crashed, crashPoint, phase, labels })
  propsRef.current = { points, multiplier, crashed, crashPoint, phase, labels }
  const domainRef = useRef({
    t: 10,
    m: 2.5,
    targetT: 10,
    targetM: 2.5,
    round: '' as string | null | undefined,
  })
  const starsRef = useRef<{ x: number; y: number; s: number; l: number }[] | null>(null)
  const skyRef = useRef<CanvasGradient | null>(null)
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 })
  const particlesRef = useRef<Particle[]>([])
  const trailRef = useRef<TrailPoint[]>([])
  const emissionRef = useRef(0)
  const motionRef = useRef<MotionState>({
    x: 0,
    y: 0,
    angle: -Math.PI / 5,
    multiplier: 1,
    initialized: false,
    lastNow: 0,
    crashX: 0,
    crashY: 0,
    crashAngle: -Math.PI / 5,
  })
  const prevPhaseRef = useRef<string | undefined>(phase)
  const crashAtRef = useRef(0)
  const reducedRef = useRef(false)

  useEffect(() => {
    const marker = new Image()
    marker.decoding = 'async'
    marker.src = flightMarker
    markerRef.current = marker
    const background = new Image()
    background.decoding = 'async'
    background.src = stageBackground
    backgroundRef.current = background
    return () => {
      markerRef.current = null
      backgroundRef.current = null
    }
  }, [])

  useEffect(() => {
    domainRef.current = { t: 10, m: 2.5, targetT: 10, targetM: 2.5, round: roundId }
    particlesRef.current = []
    trailRef.current = []
    emissionRef.current = 0
    motionRef.current.initialized = false
    motionRef.current.lastNow = 0
    motionRef.current.multiplier = 1
  }, [roundId])

  useEffect(() => {
    reducedRef.current = prefersReducedMotion()
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => {
      reducedRef.current = mq.matches
    }
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) return

    const draw = (now: number) => {
      const {
        points: ptsIn,
        multiplier: mult,
        crashed: isCrash,
        crashPoint: cp,
        phase: ph,
        labels: lbl,
      } = propsRef.current
      const reduced = reducedRef.current
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      // Fallback when TG WebView reports 0 size during first layout.
      const cssW = Math.max(canvas.clientWidth || canvas.parentElement?.clientWidth || 0, 320)
      const cssH = Math.max(canvas.clientHeight || canvas.parentElement?.clientHeight || 0, 200)
      if (cssW < 2 || cssH < 2) {
        rafRef.current = requestAnimationFrame(draw)
        return
      }
      const wantW = Math.floor(cssW * dpr)
      const wantH = Math.floor(cssH * dpr)
      const size = sizeRef.current
      if (size.w !== wantW || size.h !== wantH || size.dpr !== dpr) {
        canvas.width = wantW
        canvas.height = wantH
        sizeRef.current = { w: wantW, h: wantH, dpr }
        skyRef.current = null
        starsRef.current = null
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      const motion = motionRef.current
      const dt = motion.lastNow > 0 ? Math.min(Math.max((now - motion.lastNow) / 1000, 1 / 240), 0.05) : 1 / 60
      motion.lastNow = now

      // Crash transition: burst + flash + shake timestamps.
      if (ph === 'crashed' && prevPhaseRef.current !== 'crashed') {
        crashAtRef.current = now
        if (!reduced) {
          const pts0 = ptsIn.length ? ptsIn : [{ t: 0, m: 1 }]
          const tip0 = pts0[pts0.length - 1]
          const dom0 = domainRef.current
          const padL0 = 28
          const padR0 = 36
          const padT0 = PLOT_TOP
          const padB0 = 28
          const plotW0 = cssW - padL0 - padR0
          const plotH0 = cssH - padT0 - padB0
          const tipX0 = padL0 + (Math.min(tip0.t, dom0.t) / dom0.t) * plotW0
          const span0 = Math.max(dom0.m - 1, 0.01)
          const tipY0 =
            padT0 + plotH0 - ((Math.min(tip0.m, dom0.m) - 1) / span0) * plotH0
          motion.crashX = motion.initialized ? motion.x : tipX0
          motion.crashY = motion.initialized ? motion.y : tipY0
          motion.crashAngle = motion.angle
          for (let i = 0; i < 20; i++) {
            const a = motion.crashAngle + (Math.random() - 0.5) * Math.PI * 1.6
            const sp = 54 + Math.random() * 118
            particlesRef.current.push({
              x: motion.crashX,
              y: motion.crashY,
              vx: Math.cos(a) * sp,
              vy: Math.sin(a) * sp - 12,
              born: now,
              life: 520 + Math.random() * 360,
              size: 2.2 + Math.random() * 3.2,
              color: BURST_COLORS[i % BURST_COLORS.length],
              rotation: Math.random() * Math.PI * 2,
              spin: (Math.random() - 0.5) * 8,
              kind: 'shard',
            })
          }
        }
      }
      prevPhaseRef.current = ph

      // A short directional impact replaces the old random full-screen jitter.
      const sinceCrash = now - crashAtRef.current
      if (!reduced && ph === 'crashed' && sinceCrash < 180) {
        const k = (1 - sinceCrash / 180) * 1.6
        ctx.translate(Math.sin(sinceCrash * 0.11) * k, Math.cos(sinceCrash * 0.075) * k * 0.5)
      }

      const background = backgroundRef.current
      if (background?.complete && background.naturalWidth > 0) {
        const scale = Math.max(cssW / background.naturalWidth, cssH / background.naturalHeight)
        const drawW = background.naturalWidth * scale
        const drawH = background.naturalHeight * scale
        ctx.drawImage(background, (cssW - drawW) / 2, (cssH - drawH) / 2, drawW, drawH)
        const veil = ctx.createLinearGradient(0, 0, 0, cssH)
        veil.addColorStop(0, 'rgba(8,14,22,0.06)')
        veil.addColorStop(0.58, 'rgba(8,14,22,0.16)')
        veil.addColorStop(1, 'rgba(8,14,22,0.28)')
        ctx.fillStyle = veil
        ctx.fillRect(-8, -8, cssW + 16, cssH + 16)
      } else if (!skyRef.current) {
        const sky = ctx.createLinearGradient(0, 0, cssW, cssH)
        sky.addColorStop(0, '#1b2733')
        sky.addColorStop(0.48, '#141e29')
        sky.addColorStop(1, '#0c141e')
        skyRef.current = sky
        ctx.fillStyle = sky
        ctx.fillRect(-8, -8, cssW + 16, cssH + 16)
      } else {
        ctx.fillStyle = skyRef.current
        ctx.fillRect(-8, -8, cssW + 16, cssH + 16)
      }

      if (!starsRef.current) {
        const stars: { x: number; y: number; s: number; l: number }[] = []
        for (let i = 0; i < 26; i++) {
          stars.push({
            x: (i * 97) % cssW,
            y: ((i * 53) % (cssH * 0.7)) + 4,
            s: i % 3 === 0 ? 2 : 1.2,
            l: i % 2, // layer: 0 far / 1 near
          })
        }
        starsRef.current = stars
      }
      // Static star field: motion is reserved for the live flight itself.
      for (const s of starsRef.current) {
        ctx.fillStyle = `rgba(255,255,255,${s.l ? 0.12 : 0.055})`
        ctx.fillRect(s.x, s.y, s.s, s.s)
      }

      const pts = ptsIn.length ? ptsIn : [{ t: 0, m: 1 }]
      const last = pts[pts.length - 1]
      const lastT = Math.max(0, last.t)
      const peakM = Math.max(...pts.map((p) => p.m), mult, cp ?? 1, 1)

      // Expand axes in deliberate stages. Continuously resizing the domain made
      // the plane look pinned in place while the graph moved underneath it.
      const dom = domainRef.current
      if (ph === 'betting' || ph === 'waiting') {
        dom.t = 10
        dom.m = 2.5
        dom.targetT = 10
        dom.targetM = 2.5
      } else {
        while (lastT > dom.targetT * 0.82) dom.targetT *= 1.5
        while (peakM > 1 + (dom.targetM - 1) * 0.82) dom.targetM = 1 + (dom.targetM - 1) * 1.6
        dom.t = damp(dom.t, dom.targetT, 2.4, dt)
        dom.m = damp(dom.m, dom.targetM, 2.4, dt)
      }
      const maxT = dom.t
      const maxM = dom.m

      const padL = 28
      const padR = 36
      const padT = PLOT_TOP
      const padB = 28
      const plotW = cssW - padL - padR
      const plotH = cssH - padT - padB

      const toX = (t: number) => padL + (Math.min(t, maxT) / maxT) * plotW
      const toY = (m: number) => {
        const span = Math.max(maxM - 1, 0.01)
        const n = (Math.min(m, maxM) - 1) / span
        return padT + plotH - n * plotH
      }

      // Quiet chart grid and readable axes keep the screen feeling live,
      // without competing with the multiplier or trajectory.
      ctx.lineWidth = 1
      ctx.font = '600 10px system-ui, sans-serif'
      ctx.fillStyle = 'rgba(205,216,239,0.48)'
      ctx.textBaseline = 'top'
      ctx.textAlign = 'center'
      for (let i = 0; i <= 4; i++) {
        const x = padL + (plotW / 4) * i
        ctx.strokeStyle = i === 0 ? 'rgba(255,255,255,0.11)' : 'rgba(255,255,255,0.055)'
        ctx.beginPath()
        ctx.moveTo(x, padT)
        ctx.lineTo(x, cssH - padB)
        ctx.stroke()
        if (i > 0) ctx.fillText(`${Math.round((maxT / 4) * i)}s`, x, cssH - padB + 8)
      }
      ctx.textAlign = 'right'
      ctx.textBaseline = 'middle'
      for (let i = 0; i <= 4; i++) {
        const y = padT + (plotH / 4) * i
        ctx.strokeStyle = i === 4 ? 'rgba(255,255,255,0.11)' : 'rgba(255,255,255,0.055)'
        ctx.beginPath()
        ctx.moveTo(padL, y)
        ctx.lineTo(cssW - padR, y)
        ctx.stroke()
        if (i < 4) {
          const axisM = maxM - ((maxM - 1) / 4) * i
          ctx.fillText(`${axisM.toFixed(1)}x`, padL - 6, y)
        }
      }

      // baseline
      ctx.strokeStyle = 'rgba(255,255,255,0.08)'
      ctx.beginPath()
      ctx.moveTo(padL, toY(1))
      ctx.lineTo(cssW - padR, toY(1))
      ctx.stroke()

      // curve path (smoothed)
      const curve = new Path2D()
      traceSmooth(curve, pts, toX, toY)
      ctx.lineWidth = 2.6
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      const curveStroke = ctx.createLinearGradient(padL, cssH - padB, cssW - padR, padT)
      if (isCrash) {
        curveStroke.addColorStop(0, '#b58ab6')
        curveStroke.addColorStop(1, '#d36f9d')
      } else {
        curveStroke.addColorStop(0, '#76b8c7')
        curveStroke.addColorStop(0.55, '#818dc1')
        curveStroke.addColorStop(1, '#b181b5')
      }
      ctx.strokeStyle = curveStroke
      ctx.shadowColor = isCrash ? 'rgba(211,111,157,0.48)' : 'rgba(125,165,197,0.42)'
      ctx.shadowBlur = 8
      ctx.stroke(curve)
      ctx.shadowBlur = 0

      // fill under curve (same smoothed path, closed to baseline)
      if (pts.length > 1) {
        const fill = new Path2D(curve)
        const tip = pts[pts.length - 1]
        fill.lineTo(toX(tip.t), toY(1))
        fill.lineTo(toX(pts[0].t), toY(1))
        fill.closePath()
        const grad = ctx.createLinearGradient(0, padT, 0, cssH - padB)
        grad.addColorStop(0, isCrash ? 'rgba(211,111,157,0.2)' : 'rgba(125,165,197,0.18)')
        grad.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = grad
        ctx.fill(fill)
      }

      // Flight target from a wider tangent window avoids noisy banking when
      // authoritative points arrive at a lower cadence than the render loop.
      const tip = pts[pts.length - 1]
      const tipX = toX(tip.t)
      const tipY = toY(tip.m)
      let angle = -Math.PI / 3
      if (pts.length >= 2) {
        const prev = pts[Math.max(0, pts.length - 4)]
        const dx = toX(tip.t) - toX(prev.t)
        const dy = toY(tip.m) - toY(prev.m)
        if (Math.abs(dx) + Math.abs(dy) > 0.5) {
          angle = Math.atan2(dy, dx)
        }
      }

      const idle = ph === 'waiting' || ph === 'betting'
      const targetX = idle ? padL + 42 : tipX + Math.cos(angle) * 16
      const targetY = idle ? toY(1) - 24 : tipY + Math.sin(angle) * 16
      const targetAngle = idle ? -Math.PI / 7 : angle
      if (!motion.initialized) {
        motion.x = targetX
        motion.y = targetY
        motion.angle = targetAngle
        motion.multiplier = mult
        motion.initialized = true
      } else if (idle || reduced) {
        motion.x = targetX
        motion.y = targetY
        motion.angle = targetAngle
        motion.multiplier = mult
      } else if (ph === 'running') {
        motion.x = damp(motion.x, targetX, 14.5, dt)
        motion.y = damp(motion.y, targetY, 14.5, dt)
        motion.angle = dampAngle(motion.angle, targetAngle, 9.5, dt)
        motion.multiplier = damp(motion.multiplier, mult, 14, dt)
      } else if (isCrash && cp != null) {
        motion.multiplier = damp(motion.multiplier, cp, 18, dt)
      }

      const trail = trailRef.current
      if (!reduced && ph === 'running') {
        const latest = trail[trail.length - 1]
        const distance = latest ? Math.hypot(motion.x - latest.x, motion.y - latest.y) : Infinity
        if (distance >= 3.5) {
          trail.push({ x: motion.x, y: motion.y, angle: motion.angle, born: now })
        }
      } else if (idle) {
        trail.length = 0
      }
      while (trail.length > 44 || (trail[0] && now - trail[0].born > 1050)) trail.shift()

      // Layered vellum wake: three restrained ribbons with a tapered tail.
      if (trail.length > 2) {
        const wakePath = new Path2D()
        wakePath.moveTo(trail[0].x, trail[0].y)
        for (let i = 1; i < trail.length - 1; i++) {
          const current = trail[i]
          const next = trail[i + 1]
          wakePath.quadraticCurveTo(current.x, current.y, (current.x + next.x) / 2, (current.y + next.y) / 2)
        }
        const tail = trail[0]
        const head = trail[trail.length - 1]
        const wakeLayers = [
          { width: 18, head: 'rgba(117,214,229,0.18)', mid: 'rgba(117,214,229,0.045)' },
          { width: 10, head: 'rgba(239,165,192,0.30)', mid: 'rgba(239,165,192,0.075)' },
          { width: 3, head: 'rgba(255,139,121,0.76)', mid: 'rgba(255,139,121,0.14)' },
        ]
        for (const layer of wakeLayers) {
          const gradient = ctx.createLinearGradient(tail.x, tail.y, head.x, head.y)
          gradient.addColorStop(0, 'rgba(0,0,0,0)')
          gradient.addColorStop(0.48, layer.mid)
          gradient.addColorStop(1, layer.head)
          ctx.strokeStyle = gradient
          ctx.lineWidth = layer.width
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.stroke(wakePath)
        }
      }

      const marker = markerRef.current
      const drawMarker = (x: number, y: number, heading: number, size: number, alpha: number) => {
        if (!marker?.complete || marker.naturalWidth <= 0 || alpha <= 0) return
        const aspect = marker.naturalWidth / Math.max(marker.naturalHeight, 1)
        const drawWidth = size
        const drawHeight = size / aspect
        ctx.save()
        ctx.translate(x, y)
        ctx.rotate(heading - SPRITE_HEADING)
        ctx.globalAlpha = alpha * 0.16
        ctx.shadowColor = 'rgba(255,126,104,0.44)'
        ctx.shadowBlur = 14
        ctx.drawImage(marker, -drawWidth * 0.5, -drawHeight * 0.5, drawWidth, drawHeight)
        ctx.globalAlpha = alpha
        ctx.shadowBlur = 2.5
        ctx.drawImage(marker, -drawWidth * 0.5, -drawHeight * 0.5, drawWidth, drawHeight)
        ctx.restore()
      }

      const progress = Math.min(1, lastT / 8)
      const markerSize = idle
        ? Math.max(82, Math.min(98, cssW * 0.12))
        : Math.max(104, Math.min(134, cssW * 0.15))
      if (!isCrash) {
        drawMarker(motion.x, motion.y, motion.angle, markerSize * (0.92 + progress * 0.08), 1)
      } else if (!reduced && sinceCrash < 260) {
        const exit = Math.min(1, sinceCrash / 260)
        const eased = 1 - Math.pow(1 - exit, 3)
        const drift = 32 * eased
        drawMarker(
          motion.crashX + Math.cos(motion.crashAngle) * drift,
          motion.crashY + Math.sin(motion.crashAngle) * drift,
          motion.crashAngle + eased * 0.16,
          markerSize * (1 - eased * 0.08),
          1 - eased,
        )
      }

      // Sparse motion motes are emitted by time, not by frame rate.
      if (!reduced && ph === 'running' && now - emissionRef.current >= 64) {
        emissionRef.current = now
        const tailX = motion.x - Math.cos(motion.angle) * markerSize * 0.34
        const tailY = motion.y - Math.sin(motion.angle) * markerSize * 0.34
        for (let i = 0; i < 1; i++) {
          particlesRef.current.push({
            x: tailX + (Math.random() - 0.5) * 4,
            y: tailY + (Math.random() - 0.5) * 4,
            vx: -Math.cos(motion.angle) * 20 + (Math.random() - 0.5) * 8,
            vy: -Math.sin(motion.angle) * 20 + (Math.random() - 0.5) * 8,
            born: now,
            life: 360 + Math.random() * 160,
            size: 0.9 + Math.random() * 1.1,
            color: TRAIL_COLORS[(Math.random() * TRAIL_COLORS.length) | 0],
            rotation: motion.angle,
            spin: (Math.random() - 0.5) * 2,
            kind: 'mote',
          })
        }
      }

      // Step + paint particles.
      const parts = particlesRef.current
      for (let i = parts.length - 1; i >= 0; i--) {
        const p = parts[i]
        const age = now - p.born
        if (age >= p.life) {
          parts.splice(i, 1)
          continue
        }
        const step = dt
        p.x += p.vx * step
        p.y += p.vy * step
        p.rotation += p.spin * step
        if (p.kind === 'shard') p.vy += 48 * step
        const k = 1 - age / p.life
        ctx.globalAlpha = k * (p.kind === 'shard' ? 0.82 : 0.58)
        ctx.fillStyle = p.color
        if (p.kind === 'shard') {
          ctx.save()
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rotation)
          const shardSize = p.size * (0.55 + k * 0.45)
          ctx.beginPath()
          ctx.moveTo(shardSize, 0)
          ctx.lineTo(-shardSize * 0.65, shardSize * 0.45)
          ctx.lineTo(-shardSize * 0.3, -shardSize * 0.7)
          ctx.closePath()
          ctx.fill()
          ctx.restore()
        } else {
          ctx.beginPath()
          ctx.ellipse(p.x, p.y, p.size * (0.5 + k * 0.5), p.size * 0.5, p.rotation, 0, Math.PI * 2)
          ctx.fill()
        }
      }
      ctx.globalAlpha = 1

      // Controlled fold rupture: local light bloom and a single expanding ring.
      if (!reduced && ph === 'crashed' && sinceCrash < 460) {
        const k = 1 - sinceCrash / 460
        const originX = motion.crashX || tipX
        const originY = motion.crashY || tipY
        const g = ctx.createRadialGradient(originX, originY, 0, originX, originY, cssW * 0.28)
        g.addColorStop(0, `rgba(211,111,157,${0.22 * k})`)
        g.addColorStop(1, 'rgba(211,111,157,0)')
        ctx.fillStyle = g
        ctx.fillRect(0, 0, cssW, cssH)
        ctx.globalAlpha = k * 0.65
        ctx.strokeStyle = '#e5c4e7'
        ctx.lineWidth = 1.4
        ctx.beginPath()
        ctx.arc(originX, originY, 12 + (1 - k) * 42, 0, Math.PI * 2)
        ctx.stroke()
        ctx.globalAlpha = 1
      }

      // multiplier label
      const hudX = cssW * 0.43
      ctx.fillStyle = isCrash ? '#efb2cf' : '#ffffff'
      ctx.font = `900 ${Math.max(48, Math.min(62, cssW * 0.14))}px system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.shadowColor = isCrash ? 'rgba(199,89,143,0.34)' : 'rgba(109,157,190,0.26)'
      ctx.shadowBlur = 18
      const shownMultiplier = isCrash && cp != null ? cp : motion.multiplier
      ctx.fillText(`${shownMultiplier.toFixed(2)}x`, hudX, cssH / 2 - 6)
      ctx.shadowBlur = 0

      if (isCrash && cp != null) {
        ctx.font = '600 15px system-ui, sans-serif'
        ctx.fillStyle = '#e9b9cf'
        ctx.fillText(lbl.crashAt(cp), hudX, cssH / 2 + 28)
      } else if (ph === 'betting' || ph === 'waiting') {
        ctx.font = '600 13px system-ui, sans-serif'
        ctx.fillStyle = '#c2cbe0'
        ctx.fillText(lbl.waiting, hudX, cssH / 2 + 28)
      } else if (ph === 'running') {
        ctx.font = '600 13px system-ui, sans-serif'
        ctx.fillStyle = '#bed5e2'
        ctx.fillText(lbl.flying, hudX, cssH / 2 + 28)
      }

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  return (
    <div className="chart-wrap">
      <canvas ref={canvasRef} className="chart-canvas" aria-label={labels.aria} />
    </div>
  )
})
